import { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Palette, Sparkles, Image as ImageIcon, Type, Check, Download, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import {
    BRAND_PALETTES,
    BRAND_STYLES,
    FONT_OPTIONS,
    FONT_SLOTS,
    DEFAULT_PALETTE_ID,
    DEFAULT_STYLE_ID,
    getDefaultFontsForStyle,
} from "../lib/brandStyles";

/**
 * Mini-wizard prima della generazione del PDF (Magic Menu o Piatti del Giorno):
 * 1. Scegli intestazione: Logo o Nome ristorante
 * 2. Scegli palette colori (8 preset)
 * 3. Scegli stile (Moderno / Classico / Vintage / Street Food)
 *
 * Chiama `onConfirm({ useLogo, logoDataUrl, paletteId, styleId })`.
 */
export default function BrandWizardDialog({
    open,
    onOpenChange,
    onConfirm,
    title = "Personalizza il tuo PDF",
    confirmLabel = "Genera PDF",
}) {
    const { user } = useAuth();
    const hasLogo = !!(user?.restaurant_logo_url);
    const restaurantName = user?.restaurant_name || "";

    const [useLogo, setUseLogo] = useState(hasLogo);
    const [paletteId, setPaletteId] = useState(
        user?.brand_color || DEFAULT_PALETTE_ID
    );
    const [styleId, setStyleId] = useState(
        user?.brand_style || DEFAULT_STYLE_ID
    );
    const [fontOverrides, setFontOverrides] = useState({});

    useEffect(() => {
        if (open) {
            setUseLogo(hasLogo);
            setPaletteId(user?.brand_color || DEFAULT_PALETTE_ID);
            setStyleId(user?.brand_style || DEFAULT_STYLE_ID);
            setFontOverrides({});
        }
    }, [open, hasLogo, user]);

    // I font correnti (override se presente, altrimenti default dello stile)
    const styleDefaults = getDefaultFontsForStyle(styleId);
    const effectiveFonts = {};
    for (const slot of FONT_SLOTS) {
        effectiveFonts[slot.key] =
            fontOverrides[slot.key] || styleDefaults[slot.key];
    }

    const resetFonts = () => setFontOverrides({});

    const confirm = () => {
        if (useLogo && !hasLogo) {
            toast.error("Carica prima un logo dalla Dashboard");
            return;
        }
        onConfirm({
            useLogo,
            logoDataUrl: user?.restaurant_logo_url || "",
            paletteId,
            styleId,
            fontOverrides,
        });
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl bg-white p-0 overflow-hidden max-h-[92vh] flex flex-col"
                data-testid="brand-wizard-dialog"
            >
                <DialogHeader className="px-6 pt-5 pb-4 border-b border-brand-line bg-gradient-to-r from-brand-gold/15 to-paper">
                    <div className="flex items-start gap-3">
                        <div className="h-11 w-11 rounded-full bg-brand-navy text-brand-gold flex items-center justify-center shrink-0">
                            <Palette size={20} />
                        </div>
                        <div className="min-w-0">
                            <DialogTitle className="font-display text-2xl text-brand-ink leading-tight">
                                {title}
                            </DialogTitle>
                            <DialogDescription className="text-sm text-brand-muted mt-1">
                                Scegli intestazione, colori e stile prima di scaricare il PDF.
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Step 1 — Logo vs Nome */}
                    <Section
                        icon={<ImageIcon size={13} />}
                        step={1}
                        label="Intestazione"
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <ChoiceCard
                                active={useLogo}
                                onClick={() => setUseLogo(true)}
                                disabled={!hasLogo}
                                testid="brand-use-logo"
                                title="Logo del ristorante"
                                subtitle={
                                    hasLogo
                                        ? "Usa il logo caricato"
                                        : "Carica prima un logo dalla Dashboard"
                                }
                                preview={
                                    hasLogo ? (
                                        <img
                                            src={user.restaurant_logo_url}
                                            alt="Logo"
                                            className="h-12 w-auto object-contain"
                                        />
                                    ) : (
                                        <div className="h-12 w-12 rounded bg-brand-line/40 flex items-center justify-center text-brand-muted">
                                            <ImageIcon size={20} />
                                        </div>
                                    )
                                }
                            />
                            <ChoiceCard
                                active={!useLogo}
                                onClick={() => setUseLogo(false)}
                                testid="brand-use-name"
                                title="Nome del ristorante"
                                subtitle={
                                    restaurantName ||
                                    "Imposta il nome nel profilo per personalizzare"
                                }
                                preview={
                                    <div className="font-display italic text-lg text-brand-ink truncate max-w-[160px]">
                                        {restaurantName || "Il tuo locale"}
                                    </div>
                                }
                            />
                        </div>
                    </Section>

                    {/* Step 2 — Palette */}
                    <Section
                        icon={<Sparkles size={13} />}
                        step={2}
                        label="Palette colori"
                    >
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                            {BRAND_PALETTES.map((p) => (
                                <button
                                    key={p.id}
                                    type="button"
                                    onClick={() => setPaletteId(p.id)}
                                    data-testid={`brand-palette-${p.id}`}
                                    className={`group relative rounded-xl border p-2.5 text-left transition ${
                                        paletteId === p.id
                                            ? "border-brand-navy shadow-md bg-paper"
                                            : "border-brand-line hover:border-brand-gold/60"
                                    }`}
                                >
                                    <div className="flex items-center gap-1.5 mb-1.5">
                                        <span
                                            className="h-6 w-6 rounded-full border border-black/10"
                                            style={{ background: p.primary }}
                                        />
                                        <span
                                            className="h-6 w-6 rounded-full border border-black/10"
                                            style={{ background: p.accent }}
                                        />
                                    </div>
                                    <div className="text-[11px] font-bold text-brand-ink leading-tight">
                                        {p.label}
                                    </div>
                                    {paletteId === p.id && (
                                        <Check
                                            size={12}
                                            className="absolute top-1.5 right-1.5 text-brand-navy"
                                        />
                                    )}
                                </button>
                            ))}
                        </div>
                    </Section>

                    {/* Step 3 — Style */}
                    <Section
                        icon={<Type size={13} />}
                        step={3}
                        label="Stile grafico"
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                            {BRAND_STYLES.map((s) => (
                                <button
                                    key={s.id}
                                    type="button"
                                    onClick={() => setStyleId(s.id)}
                                    data-testid={`brand-style-${s.id}`}
                                    className={`text-left rounded-xl border p-3 transition ${
                                        styleId === s.id
                                            ? "border-brand-navy bg-paper shadow"
                                            : "border-brand-line hover:border-brand-gold/60"
                                    }`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="font-display text-base text-brand-ink">
                                            {s.label}
                                        </div>
                                        {styleId === s.id && (
                                            <Check
                                                size={13}
                                                className="text-brand-navy shrink-0"
                                            />
                                        )}
                                    </div>
                                    <div className="text-[11px] text-brand-muted leading-snug mt-0.5">
                                        {s.description}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </Section>

                    {/* Step 4 — Font per ogni elemento */}
                    <Section
                        icon={<Type size={13} />}
                        step={4}
                        label="Font (per ogni elemento)"
                        right={
                            <button
                                type="button"
                                onClick={resetFonts}
                                data-testid="brand-fonts-reset"
                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-muted hover:text-brand-navy transition"
                                title="Ripristina i font predefiniti dello stile selezionato"
                            >
                                <RotateCcw size={11} /> Default dello stile
                            </button>
                        }
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                            {FONT_SLOTS.map((slot) => (
                                <FontPicker
                                    key={slot.key}
                                    slot={slot}
                                    value={effectiveFonts[slot.key]}
                                    onChange={(fontId) =>
                                        setFontOverrides((p) => ({
                                            ...p,
                                            [slot.key]: fontId,
                                        }))
                                    }
                                    overridden={!!fontOverrides[slot.key]}
                                />
                            ))}
                        </div>
                    </Section>
                </div>

                <div className="px-6 py-4 bg-brand-cream border-t border-brand-line flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-[11px] text-brand-muted">
                        Le preferenze vengono usate solo per questo PDF (non vengono salvate
                        come default).
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => onOpenChange(false)}
                            className="px-4 py-2 rounded-full text-sm font-semibold text-brand-muted hover:text-brand-ink transition"
                        >
                            Annulla
                        </button>
                        <button
                            type="button"
                            onClick={confirm}
                            data-testid="brand-confirm"
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-navy text-white text-sm font-bold hover:bg-brand-navyLight transition shadow"
                        >
                            <Download size={14} /> {confirmLabel}
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function Section({ icon, step, label, children, right }) {
    return (
        <div>
            <div className="flex items-center justify-between gap-2 mb-2.5">
                <div className="flex items-center gap-2">
                    <span className="h-5 w-5 rounded-full bg-brand-navy text-white text-[10px] font-bold flex items-center justify-center">
                        {step}
                    </span>
                    <span className="text-brand-gold">{icon}</span>
                    <span className="eyebrow text-brand-ink">{label}</span>
                </div>
                {right}
            </div>
            {children}
        </div>
    );
}

function FontPicker({ slot, value, onChange, overridden }) {
    const previewFont = FONT_OPTIONS.find((f) => f.id === value) || FONT_OPTIONS[0];
    const previewCss = fontPreviewCss(previewFont);
    return (
        <div
            className={`rounded-lg border p-2.5 transition ${
                overridden
                    ? "border-brand-navy/50 bg-paper"
                    : "border-brand-line bg-white"
            }`}
            data-testid={`brand-font-slot-${slot.key}`}
        >
            <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="text-[10px] uppercase tracking-wider text-brand-muted font-semibold">
                    {slot.label}
                </div>
                {overridden && (
                    <span className="text-[9px] uppercase tracking-wider bg-brand-navy/10 text-brand-navy px-1.5 py-0.5 rounded">
                        Personalizzato
                    </span>
                )}
            </div>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={`brand-font-select-${slot.key}`}
                className="w-full px-2 py-1.5 rounded-md bg-white border border-brand-line text-[12px] focus:outline-none focus:border-brand-gold"
            >
                {FONT_OPTIONS.map((f) => (
                    <option key={f.id} value={f.id}>
                        {f.label}
                    </option>
                ))}
            </select>
            <div
                className="mt-1.5 text-[14px] text-brand-ink truncate"
                style={previewCss}
                aria-hidden="true"
            >
                {fontPreviewText(slot.key)}
            </div>
        </div>
    );
}

function fontPreviewCss(font) {
    const family =
        font.family === "times"
            ? '"Times New Roman", Times, serif'
            : font.family === "courier"
            ? '"Courier New", Courier, monospace'
            : "Helvetica, Arial, sans-serif";
    const weight = font.weight.includes("bold") ? 700 : 400;
    const italic = font.weight.includes("italic") ? "italic" : "normal";
    return { fontFamily: family, fontWeight: weight, fontStyle: italic };
}

function fontPreviewText(slotKey) {
    switch (slotKey) {
        case "title": return "Il tuo Menù";
        case "subtitle": return "Lo Chef Consiglia";
        case "dish": return "Spaghetti alla Carbonara";
        case "price": return "12,50 €";
        case "allergens": return "allergeni: glutine · uova";
        default: return "Aa Bb Cc";
    }
}

function ChoiceCard({ active, onClick, disabled, title, subtitle, preview, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            data-testid={testid}
            className={`flex items-center gap-3 rounded-xl border p-3 transition text-left ${
                active
                    ? "border-brand-navy bg-paper shadow"
                    : "border-brand-line hover:border-brand-gold/60"
            } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
        >
            <div className="h-12 w-16 rounded bg-brand-cream/60 flex items-center justify-center shrink-0 overflow-hidden">
                {preview}
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-brand-ink truncate">{title}</div>
                <div className="text-[11px] text-brand-muted leading-snug">
                    {subtitle}
                </div>
            </div>
            {active && <Check size={14} className="text-brand-navy shrink-0" />}
        </button>
    );
}
